import { DeleteVideoDto } from "@repo/api/video";
import { AmplifilesClient } from "./api-client";
import {
  VoiceOverDto,
  VideoDto,
  UpdateVideoDto,
  CreateVoiceOverDto,
  FetchRealEstateImagesResponseDto,
  GetVoiceOversByExtIdsResponseDto,
  DuplicateVideoResponseDto,
  CreateVideoDto,
} from "@repo/api/editor";
import { AnalyticsApi } from "./analytics-api";
import {
  DeleteOverlayTemplateResponseDto,
  OverlayTemplateDto,
  UpsertOverlayTemplateDto,
} from "@repo/api/overlays";
import {
  VoiceActorConfig,
  VoiceActorDto,
  VoiceActorPreviouslyUsedDto,
} from "@repo/api/voice-actor";
import {
  AssetMediaStatus,
  GetAssetMediasResponseDto,
} from "@repo/api/asset-media";
import {
  DeleteFontResponseDto,
  FontDto,
  GetPagedFontsQueryDto,
  ListFontsResponseDto,
  PagedFontsResponseDto,
} from "@repo/api/font";

export class EditorApi {
  static async createVideo(
    teamExternalId: string,
    data: CreateVideoDto,
  ): Promise<VideoDto> {
    const response = await AmplifilesClient.instance.post(
      `/team/${teamExternalId}/editor/video`,
      data,
    );

    AnalyticsApi.logVideoCreated();

    return VideoDto.parse(response.data);
  }

  static async deleteVideo(
    teamExternalId: string,
    videoExternalId: string,
  ): Promise<DeleteVideoDto> {
    const response = await AmplifilesClient.instance.post(
      `/team/${teamExternalId}/editor/video/${videoExternalId}/delete`,
    );

    return DeleteVideoDto.parse(response.data);
  }

  static async duplicateVideo(
    teamExternalId: string,
    videoExternalId: string,
  ): Promise<DuplicateVideoResponseDto> {
    const response = await AmplifilesClient.instance.post(
      `/team/${teamExternalId}/editor/video/${videoExternalId}/duplicate`,
    );

    return DuplicateVideoResponseDto.parse(response.data);
  }

  static async updateVideo(
    teamExternalId: string,
    videoExternalId: string,
    video: UpdateVideoDto,
  ): Promise<VideoDto> {
    const response = await AmplifilesClient.instance.patch(
      `/team/${teamExternalId}/editor/video/${videoExternalId}`,
      video,
    );
    return VideoDto.parse(response.data);
  }

  static async getVideo(
    teamExternalId: string,
    videoExternalId: string,
  ): Promise<VideoDto> {
    const response = await AmplifilesClient.instance.get(
      `/team/${teamExternalId}/editor/video/${videoExternalId}`,
    );

    return VideoDto.parse(response.data);
  }

  static async getVoiceOversByExtIds(
    teamExternalId: string,
    externalIds: string[],
  ): Promise<GetVoiceOversByExtIdsResponseDto> {
    if (externalIds.length === 0) {
      return [];
    }

    const response = await AmplifilesClient.instance.post(
      `/team/${teamExternalId}/voice-over-by-ext-ids`,
      { externalIds },
    );

    return GetVoiceOversByExtIdsResponseDto.parse(response.data);
  }

  static async createVoiceOver(
    teamExternalId: string,
    videoExternalId: string,
    voiceOver: CreateVoiceOverDto,
  ): Promise<VoiceOverDto> {
    const response = await AmplifilesClient.instance.post(
      `/team/${teamExternalId}/editor/video/${videoExternalId}/voice-over`,
      voiceOver,
    );

    AnalyticsApi.logVoiceOverAdded();

    return VoiceOverDto.parse(response.data);
  }

  static async getPreviouslyUsedVoiceActors(
    teamExternalId: string,
  ): Promise<VoiceActorPreviouslyUsedDto> {
    const response = await AmplifilesClient.instance.get(
      `/team/${teamExternalId}/editor/voice-actor/previously-used`,
    );

    return VoiceActorPreviouslyUsedDto.parse(response.data);
  }

  static async getVoiceActors(
    teamExternalId: string,
  ): Promise<VoiceActorDto[]> {
    const response = await AmplifilesClient.instance.get(
      `/team/${teamExternalId}/editor/voice-actor`,
    );

    return VoiceActorDto.array().parse(response.data);
  }

  static async createVoiceActor(
    teamExternalId: string,
    config: VoiceActorConfig,
    isGlobal: boolean,
  ): Promise<VoiceActorDto> {
    const response = await AmplifilesClient.instance.post(
      `/team/${teamExternalId}/editor/voice-actor`,
      {
        config,
        isGlobal,
      },
    );

    return VoiceActorDto.parse(response.data);
  }

  static async updateVoiceActor(
    teamExternalId: string,
    voiceActorExternalId: string,
    isGlobal: boolean,
  ): Promise<VoiceActorDto> {
    const response = await AmplifilesClient.instance.post(
      `/team/${teamExternalId}/editor/voice-actor/${voiceActorExternalId}`,
      {
        isGlobal,
      },
    );

    return VoiceActorDto.parse(response.data);
  }

  static async getPagedFonts(
    teamExternalId: string,
    query: GetPagedFontsQueryDto,
  ): Promise<PagedFontsResponseDto> {
    const response = await AmplifilesClient.instance.get(
      `/team/${teamExternalId}/font`,
      { params: query },
    );

    return PagedFontsResponseDto.parse(response.data);
  }

  static async getFontsByExternalIds(
    teamExternalId: string,
    externalIds: string[],
  ): Promise<FontDto[]> {
    if (externalIds.length === 0) {
      return [];
    }

    const response = await AmplifilesClient.instance.post(
      `/team/${teamExternalId}/font/by-ids`,
      { externalIds },
    );

    return ListFontsResponseDto.parse(response.data).fonts;
  }

  static async deleteFont(
    teamExternalId: string,
    fontExternalId: string,
  ): Promise<DeleteFontResponseDto> {
    const response = await AmplifilesClient.instance.delete(
      `/team/${teamExternalId}/font/${fontExternalId}`,
    );

    return DeleteFontResponseDto.parse(response.data);
  }

  static async getAssetMedias(
    teamExternalId: string,
    assetMediaExternalIds: string[],
    status?: AssetMediaStatus,
  ): Promise<GetAssetMediasResponseDto> {
    const response = await AmplifilesClient.instance.post(
      `/team/${teamExternalId}/editor/asset-media`,
      { assetMediaExternalIds, status },
    );

    return GetAssetMediasResponseDto.parse(response.data);
  }

  static async fetchRealEstateListingImages(
    teamExternalId: string,
    videoExternalId: string,
    listingUrl: string,
  ): Promise<FetchRealEstateImagesResponseDto> {
    const response = await AmplifilesClient.instance.post(
      `/team/${teamExternalId}/editor/video/${videoExternalId}/fetch-real-estate-images`,
      { listingUrl },
    );

    AnalyticsApi.logListingScraped();

    return FetchRealEstateImagesResponseDto.parse(response.data);
  }

  static async createOverlayTemplate(
    teamExternalId: string,
    data: UpsertOverlayTemplateDto,
  ): Promise<OverlayTemplateDto> {
    const response = await AmplifilesClient.instance.post(
      `/team/${teamExternalId}/editor/overlay-template`,
      data,
    );

    return OverlayTemplateDto.parse(response.data);
  }

  static async getOverlayTemplates(
    teamExternalId: string,
  ): Promise<OverlayTemplateDto[]> {
    const response = await AmplifilesClient.instance.get(
      `/team/${teamExternalId}/editor/overlay-template`,
    );

    return OverlayTemplateDto.array().parse(response.data);
  }

  static async deleteOverlayTemplate(
    teamExternalId: string,
    overlayTemplateExternalId: string,
  ): Promise<DeleteOverlayTemplateResponseDto> {
    const response = await AmplifilesClient.instance.delete(
      `/team/${teamExternalId}/editor/overlay-template/${overlayTemplateExternalId}`,
    );

    return DeleteOverlayTemplateResponseDto.parse(response.data);
  }
}
